import { action } from '@ember/object';
import type Owner from '@ember/owner';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { DSSetup } from 'webapp-types';
import { CompareType, MaxValueType } from 'webapp/enums/enums';

import didUpdate from 'webapp/modifiers/did-update';
import FormInputComponent from 'webapp/components/form/input';
import FormMetricSelectComponent from 'webapp/components/form/metric-select/index';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import FormSelectAttributesComponent from 'webapp/components/form/select-attributes';
import FormSelectDimensionsComponent from 'webapp/components/form/select-dimensions';
import FormSelectUnitsComponent from 'webapp/components/form/select-units';
import GoalSelectorComponent from 'webapp/components/goal/selector';

import type Block from 'webapp/models-custom/block';
import type BlockSetup from 'webapp/models-custom/block-setup';
import type DataSelect from 'webapp/models-custom/data-select';
import DsCompare from 'webapp/models-custom/ds-compare';
import type VisualizationGauge from 'webapp/models-custom/visualization/gauge';
import type VisualizationProgress from 'webapp/models-custom/visualization/progress';
import type VisualizationRadialProgress from 'webapp/models-custom/visualization/radial-progress';

interface Args {
	blockM: Block;
	model: DataSelect;
}

export default class BlockSettingsMetricMaxValueComponent extends Component<Args> {
	@tracked maxConstValue = 0;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.refreshMaxConstValue();
	}

	get block(): Block {
		return this.args.blockM;
	}

	get blockSetup(): BlockSetup {
		return this.block.blockSetup;
	}

	get visualization() {
		return this.blockSetup.visualization as
			VisualizationGauge | VisualizationProgress | VisualizationRadialProgress;
	}

	@cached
	get dsCompare(): DsCompare | undefined {
		return this.args.model.limitations.compareCMs[this.visualization.indexForMaxValueCompare];
	}

	get options(): LabelValuePairNumeric[] {
		const options: LabelValuePairNumeric[] = [
			{
				value: MaxValueType.AUTO,
				label: 'Auto',
			},
		];

		if (this.blockSetup.visualization.allowGoalAsMaxValue) {
			options.push({
				value: MaxValueType.GOAL,
				label: 'Goal',
			});
		}

		options.push({
			value: MaxValueType.CUSTOM,
			label: 'Manual',
		});

		options.push({
			value: MaxValueType.OTHER_METRIC,
			label: 'Metric',
		});

		return options;
	}

	get selected(): MaxValueType {
		let maxValueType = MaxValueType.AUTO;

		const compare = this.dsCompare;
		if (compare && !compare.isNone) {
			if (compare.isOtherMetric) {
				maxValueType = MaxValueType.OTHER_METRIC;
			} else if (compare.isGoal) {
				maxValueType = MaxValueType.GOAL;
			} else {
				maxValueType = MaxValueType.CUSTOM;
			}
		}
		return maxValueType;
	}

	get showGoalSettings(): boolean {
		return this.selected === MaxValueType.GOAL;
	}

	get showCustomValueSettings(): boolean {
		return this.selected === MaxValueType.CUSTOM;
	}

	get showOtherMetricSettings(): boolean {
		return this.selected === MaxValueType.OTHER_METRIC;
	}

	@action
	changeSelected(selected: AnySimpleValue): void {
		const dataSelect = this.args.model;

		let compare = this.dsCompare;
		if (selected === MaxValueType.AUTO) {
			if (compare) {
				// deleting unnecessary compare
				dataSelect.limitations.removeCompare(compare);
			}
		} else {
			let maxValueCompareFormula;
			switch (selected) {
				case MaxValueType.GOAL:
					maxValueCompareFormula = { type: CompareType.GOAL };
					break;
				case MaxValueType.CUSTOM:
					maxValueCompareFormula = {
						type: CompareType.RANGE,
						config: { constValue: 10000 },
					};
					break;
				case MaxValueType.OTHER_METRIC:
					maxValueCompareFormula = { type: CompareType.OTHER_METRIC };
					break;
			}

			if (compare) {
				dataSelect.limitations.removeCompare(compare);
			}

			compare = DsCompare.createFromFormula(maxValueCompareFormula || null);
			dataSelect.limitations.addCompare(compare);
		}

		this.updateCompares(dataSelect.limitations.compareCMs);
	}

	@action
	saveMaxConstValue() {
		this.updateOnCompare((compare: DsCompare) => {
			compare.constValue = +this.maxConstValue;
		});
	}

	@action
	refreshMaxConstValue() {
		this.maxConstValue = this.dsCompare?.constValue || 0;
	}

	@action
	changeMaxMetricData(metricData: DSSetup.MetricData | null): void {
		this.updateOnCompare((compare: DsCompare) => {
			compare.metricSetup.setFromMetricData(metricData);
		});
	}

	@action
	changeDimensions(dimensions: string[]): void {
		this.updateOnCompare((compare: DsCompare) => {
			const metricSetup = compare.metricSetup;
			metricSetup.dimensions = dimensions;
			metricSetup.resetFilters();
		});
	}

	@action
	changeMaxAttribute(attribute: string | null): void {
		this.updateOnCompare((compare: DsCompare) => {
			compare.metricSetup.setFiltersFromAttributeName(attribute);
		});
	}

	@action
	changeMaxUnit(unit: string | null): void {
		this.updateOnCompare((compare: DsCompare) => {
			compare.metricSetup.unitName = unit!;
		});
	}

	private updateOnCompare(updateFn: (compare: DsCompare) => void) {
		const dataSelect = this.args.model,
			compares = dataSelect.limitations.compareCMs;

		const compare = this.dsCompare;
		if (compare) {
			updateFn(compare);
			this.updateCompares(compares);
		}
	}

	private updateCompares(compares: DsCompare[]) {
		const dataSelect = this.args.model;
		dataSelect.updateComparesFormulaArrayFromCompareModels(compares);
		this.blockSetup.saveDataSelect(dataSelect);
	}
	<template>
		<div class="row" {{didUpdate this.refreshMaxConstValue @model.limitations.compareCMs}}>
			<label>Max value</label>
			<FormRadiobuttonGroupComponent
				class="radiobutton-switcher"
				@name="maxValue"
				@selected={{this.selected}}
				@options={{this.options}}
				@onChange={{this.changeSelected}}
				@theme="dark-blue-alternate"
			/>
		</div>

		{{#if this.showGoalSettings}}
			<div class="row">
				<GoalSelectorComponent
					@selectedGoalMetricData={{this.dsCompare.metricSetup.metricData}}
					@metricSetup={{@model.metricSetup}}
					@onChange={{this.changeMaxMetricData}}
					@onlyAutomaticEnabled={{true}}
					@origin="designer"
					@isGoalTypeObjectiveDisabled={{true}}
				/>
			</div>
		{{else if this.showCustomValueSettings}}
			<FormInputComponent
				name="manualInput"
				type="number"
				@value={{this.maxConstValue}}
				@focusOut={{this.saveMaxConstValue}}
				@loseFocusOnEnter={{true}}
				@theme="dark"
			/>
		{{else if this.showOtherMetricSettings}}

			<div class="row">
				<FormMetricSelectComponent
					@selected={{this.dsCompare.metricSetup.metricData}}
					@onChange={{this.changeMaxMetricData}}
					@origin="designer"
					@placeholder="Select a metric"
					@theme="dark"
				/>
			</div>

			{{#if this.dsCompare.metricSetup.allowDimensions}}
				<div class="row">
					<label>Breakdown by</label>
					<FormSelectDimensionsComponent
						@metricSetup={{this.dsCompare.metricSetup}}
						@onChange={{this.changeDimensions}}
						@showFloatlabel={{false}}
						@theme="dark"
					/>
				</div>
			{{/if}}

			{{#if this.dsCompare.metricSetup.allowBasicDimensionValueFilters}}
				<div class="row">
					<FormSelectAttributesComponent
						@selected={{this.dsCompare.metricSetup.firstAttributeNameFromBasicFilters}}
						@metricSetupForQuery={{this.dsCompare.metricSetup}}
						@dateRangeForQuery={{@model.dateRangeCM}}
						@onChange={{this.changeMaxAttribute}}
						@isMultiple={{false}}
						@maxSelectionLength={{1}}
						@theme="dark"
					/>
				</div>
			{{/if}}

			{{#if this.dsCompare.metricSetup.allowUnitNameFilter}}
				<div class="row">
					<FormSelectUnitsComponent
						@selected={{this.dsCompare.metricSetup.unitName}}
						@metricSetupForQuery={{this.dsCompare.metricSetup}}
						@dateRangeForQuery={{@model.dateRangeCM}}
						@onChange={{this.changeMaxUnit}}
						@isMultiple={{false}}
						@maxSelectionLength={{1}}
						@theme="dark"
					/>
				</div>
			{{/if}}

		{{/if}}
	</template>
}
